// FTEDN Creator — Program Card (with eligibility + apply action)
import './ProgramCard.css';

interface Program {
  id: string;
  name: string;
  desc: string;
  status: 'accepting' | 'testing' | 'completed' | 'full' | 'closed' | string;
  spots: number;
  xp?: number;
  xpReward?: number;
  phase?: number;
  totalPhases?: number;
}

interface ProgramCardProps {
  program: Program;
  index?: number;
  eligible?: boolean;
  reason?: string;
  onApply?: () => void;
}

export function ProgramCard({ program, index = 0, eligible = true, reason, onApply }: ProgramCardProps) {
  const statusConfig: Record<string, { label: string; color: string; glow: string }> = {
    accepting: { label: '🟢 Accepting', color: 'var(--success-green)', glow: 'var(--success-glow)' },
    testing: { label: '🔬 In Testing', color: 'var(--xp-gold)', glow: 'var(--xp-gold-glow)' },
    completed: { label: '✅ Complete', color: 'var(--sage-green)', glow: 'var(--success-glow)' },
    full: { label: '🔴 Full', color: 'var(--error-red, #ef4444)', glow: 'none' },
    closed: { label: '⬛ Closed', color: 'var(--text-muted, #888)', glow: 'none' },
  };

  const config = statusConfig[program.status] ?? { label: program.status, color: 'var(--text-muted, #888)', glow: 'none' };
  const xp = program.xpReward ?? program.xp ?? 0;
  const progressPct = program.phase && program.totalPhases ? (program.phase / program.totalPhases) * 100 : 0;

  return (
    <div className="program-card glass-card" style={{ animationDelay: `${index * 0.1}s` }}>
      <div className="program-card-header">
        <span className="program-status" style={{ color: config.color, textShadow: `0 0 6px ${config.glow}` }}>
          {config.label}
        </span>
        {xp > 0 && (
          <span className="program-xp">+{xp} XP</span>
        )}
      </div>

      <h3 className="program-name">{program.name}</h3>
      <p className="program-desc">{program.desc}</p>

      {/* Phase progress bar */}
      {program.phase !== undefined && program.totalPhases && (
        <div className="program-progress">
          <div className="program-progress-track">
            <div className="program-progress-fill" style={{ width: `${progressPct}%` }} />
          </div>
          <span className="program-phase-label">Phase {program.phase}/{program.totalPhases}</span>
        </div>
      )}

      {program.status === 'accepting' && program.spots > 0 && (
        <div className="program-footer">
          <span className="program-spots">{program.spots} spots left</span>
          {eligible ? (
            <button className="neo-btn neo-btn--primary program-join-btn" onClick={onApply}>
              Join Beta
            </button>
          ) : (
            <span className="program-ineligible">{reason}</span>
          )}
        </div>
      )}

      {program.status === 'completed' && (
        <div className="program-complete-badge">
          <span>🎖️ Beta Complete — XP Earned</span>
        </div>
      )}
    </div>
  );
}
